import { useEffect, useRef, useState } from "react";
import { MessageSquare, Send, Plus, X, Paperclip, ImageIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function priorityPill(p) {
  if (p === "urgent") return { c: "#f87171", b: "rgba(229,75,89,0.35)", g: "rgba(229,75,89,0.12)" };
  if (p === "high")   return { c: "#fca5a5", b: "rgba(244,180,49,0.35)", g: "rgba(244,180,49,0.12)" };
  if (p === "low")    return { c: "#cbd5e1", b: "rgba(148,163,184,0.32)", g: "rgba(148,163,184,0.10)" };
  return { c: "#6ee7b7", b: "rgba(16,185,129,0.35)", g: "rgba(16,185,129,0.12)" };
}
function statusPill(s) {
  const map = {
    open:        { c: "#fca5a5", b: "rgba(244,180,49,0.35)", g: "rgba(244,180,49,0.12)" },
    in_progress: { c: "#93c5fd", b: "rgba(59,130,246,0.35)", g: "rgba(59,130,246,0.12)" },
    answered:    { c: "#6ee7b7", b: "rgba(16,185,129,0.35)", g: "rgba(16,185,129,0.12)" },
    closed:      { c: "#cbd5e1", b: "rgba(148,163,184,0.32)", g: "rgba(148,163,184,0.10)" },
  };
  return map[s] || map.open;
}

function attachmentSrc(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

export default function PromoTickets() {
  const [rows, setRows] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ subject: "", message: "", priority: "normal", attachment_url: "" });
  const [reply, setReply] = useState("");
  const [replyAttach, setReplyAttach] = useState("");
  const fileRef = useRef(null);
  const replyFileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const r = await api.get("/aff-panel/tickets"); setRows(r.data?.rows || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const uploadFile = async (file) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const token = localStorage.getItem("ss_promo_token");
      const res = await fetch(`${BACKEND_URL}/api/aff-panel/tickets/upload-attachment`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      toast.success("Image uploaded");
      return data.url || "";
    } catch (e) {
      toast.error(e.message || "Upload failed");
      return null;
    } finally { setUploading(false); }
  };

  const onCreateFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const url = await uploadFile(f);
    if (url) setForm((s) => ({ ...s, attachment_url: url }));
  };
  const onReplyFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const url = await uploadFile(f);
    if (url) setReplyAttach(url);
  };

  const create = async () => {
    if (!form.subject || !form.message) return toast.error("Subject and message are required");
    try {
      const r = await api.post("/aff-panel/tickets", form);
      setOpenCreate(false);
      setForm({ subject: "", message: "", priority: "normal", attachment_url: "" });
      toast.success("Ticket submitted");
      await load();
      setActive(r.data?.ticket);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendReply = async () => {
    if (!active || (!reply && !replyAttach)) return;
    try {
      const r = await api.post(`/aff-panel/tickets/${active.id}/reply`, { message: reply || "(attachment)", attachment_url: replyAttach });
      setActive(r.data?.ticket);
      setReply("");
      setReplyAttach("");
      await load();
      toast.success("Reply sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const closeTicket = async () => {
    if (!active) return;
    try {
      await api.post(`/aff-panel/tickets/${active.id}/close`);
      toast.success("Ticket closed");
      await load();
      setActive(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="SUPPORT"
        title="Support Tickets"
        subtitle="Direct line to your dedicated affiliate concierge. Attach screenshots when helpful."
        right={(
          <button onClick={() => setOpenCreate(true)} className="p-btn-gold inline-flex items-center gap-2 px-4 h-10 rounded-xl text-[13px] font-bold" data-testid="open-ticket-btn">
            <Plus size={14} /> New Ticket
          </button>
        )}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5">
          {rows.length === 0 ? (
            <div className="p-glass p-10 text-center">
              <MessageSquare size={28} className="mx-auto text-emerald-200 mb-2" />
              <div className="text-white font-bold">No tickets yet</div>
              <div className="text-white/55 text-sm">Open your first ticket and we'll be in touch shortly.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((t) => {
                const sp = statusPill(t.status);
                const pp = priorityPill(t.priority);
                const isActive = active?.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActive(t)}
                    className="w-full text-left p-4 rounded-xl transition-all"
                    style={{
                      background: isActive ? "rgba(34, 192, 115, 0.10)" : "rgba(255,255,255,0.02)",
                      border: `1px solid ${isActive ? "rgba(34, 192, 115, 0.55)" : "rgba(255,255,255,0.08)"}`,
                    }}
                    data-testid={`ticket-row-${t.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-white truncate">{t.subject}</div>
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-md" style={{ color: sp.c, background: sp.g, border: `1px solid ${sp.b}` }}>{t.status}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-md" style={{ color: pp.c, background: pp.g, border: `1px solid ${pp.b}` }}>{t.priority}</span>
                      <span className="text-[11px] text-white/45">{(t.messages || []).length} msg</span>
                      <span className="ml-auto text-[11px] text-white/45">{(t.updated_at || "").slice(0, 10)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="lg:col-span-7">
          {active ? (
            <div className="p-glass" style={{ padding: 18 }}>
              <div className="flex items-center justify-between gap-3 mb-3 pb-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold">Ticket</div>
                  <div className="font-black text-white text-lg truncate">{active.subject}</div>
                </div>
                {active.status !== "closed" && (
                  <button onClick={closeTicket} className="p-btn-ghost px-3 h-9 rounded-lg text-[11px] inline-flex items-center gap-1.5" data-testid="ticket-close">
                    <CheckCircle2 size={12} /> Close
                  </button>
                )}
                <button onClick={() => setActive(null)} className="p-btn-ghost p-2 rounded-lg"><X size={14} /></button>
              </div>
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                {(active.messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "affiliate" ? "justify-end" : "justify-start"}`}>
                    <div
                      className="max-w-[80%] p-3 rounded-xl"
                      style={{
                        background: m.sender === "affiliate" ? "linear-gradient(135deg, rgba(34, 192, 115, 0.18), rgba(127, 29, 29, 0.06))" : "rgba(255,255,255,0.04)",
                        border: `1px solid ${m.sender === "affiliate" ? "rgba(34, 192, 115, 0.4)" : "rgba(255,255,255,0.08)"}`,
                      }}
                    >
                      <div className="text-[10px] font-bold uppercase tracking-[0.22em] mb-1" style={{ color: m.sender === "affiliate" ? "#a7f3d0" : "#93c5fd" }}>
                        {m.sender === "affiliate" ? "You" : "Concierge"} • {(m.created_at || "").slice(11, 16)}
                      </div>
                      <div className="text-white text-[13.5px] whitespace-pre-wrap">{m.body}</div>
                      {m.attachment_url && (
                        <a href={attachmentSrc(m.attachment_url)} target="_blank" rel="noreferrer" className="block mt-2">
                          <img src={attachmentSrc(m.attachment_url)} alt="attachment" className="max-h-[180px] rounded-lg border border-white/10" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {active.status !== "closed" && (
                <div className="mt-4 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                  {replyAttach && (
                    <div className="mb-2 inline-flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px]"
                         style={{ background: "rgba(34,192,115,0.08)", border: "1px solid rgba(34,192,115,0.3)", color: "#a7f3d0" }}>
                      <ImageIcon size={11} /> Image attached
                      <button className="text-white/55 hover:text-white" onClick={() => setReplyAttach("")}><X size={10} /></button>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea
                      rows={2}
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Type your reply…"
                      className="flex-1 p-input-shell rounded-xl px-3 py-2 text-[13.5px] resize-none"
                      data-testid="ticket-reply-input"
                    />
                    <input ref={replyFileRef} type="file" accept="image/*" hidden onChange={onReplyFile} />
                    <button onClick={() => replyFileRef.current?.click()} disabled={uploading} className="p-btn-ghost px-3 h-11 rounded-xl inline-flex items-center gap-1.5 text-[12px]" data-testid="ticket-reply-attach">
                      <Paperclip size={13} />
                    </button>
                    <button onClick={sendReply} className="p-btn-gold px-4 h-11 rounded-xl text-[12px] font-bold inline-flex items-center gap-2" data-testid="ticket-reply-send">
                      <Send size={14} /> Send
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-glass p-10 text-center text-white/45">Select a ticket on the left to view conversation.</div>
          )}
        </div>
      </div>

      {openCreate && (
        <div className="p-modal-overlay" onClick={() => setOpenCreate(false)}>
          <div className="p-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-200 font-bold">NEW TICKET</div>
              <button onClick={() => setOpenCreate(false)} className="p-btn-ghost p-2 rounded-lg"><X size={14} /></button>
            </div>
            <h3 className="font-black text-white text-xl p-gold-text mb-3">Tell us how we can help</h3>
            <div className="space-y-3">
              <div>
                <label className="p-label">Subject</label>
                <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="p-input-shell rounded-xl px-3 h-11 w-full text-[14px]" data-testid="ticket-subject" />
              </div>
              <div>
                <label className="p-label">Priority</label>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="p-input-shell rounded-xl px-3 h-11 w-full text-[14px]" data-testid="ticket-priority">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="p-label">Message</label>
                <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="p-input-shell rounded-xl px-3 py-2 w-full text-[14px] resize-none" data-testid="ticket-message" />
              </div>
              <div>
                <label className="p-label">Attach screenshot (optional)</label>
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onCreateFile} />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="p-btn-ghost px-3 h-10 rounded-xl inline-flex items-center gap-1.5 text-[12px]" data-testid="ticket-attach-btn">
                    <Paperclip size={12} /> {uploading ? "Uploading…" : "Choose Image"}
                  </button>
                  {form.attachment_url && (
                    <div className="inline-flex items-center gap-2 text-emerald-300 text-[11.5px]">
                      <ImageIcon size={11} /> Attached
                      <button className="text-white/55 hover:text-white" onClick={() => setForm((s) => ({ ...s, attachment_url: "" }))}><X size={11} /></button>
                    </div>
                  )}
                </div>
              </div>
              <button onClick={create} className="p-btn-gold w-full h-11 rounded-xl text-[13px] font-bold inline-flex items-center justify-center gap-2" data-testid="ticket-submit">
                <Send size={14} /> Submit Ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
